/**
 * Messages Page
 * Phase 6: Communication & Engagement
 * Full messaging interface with inbox and conversation view
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { MessageInbox } from '../components/MessageInbox';
import { ConversationThread } from '../components/ConversationThread';
import { MessageComposer } from '../components/MessageComposer';
import { messagingService } from '../services/messagingService';
import { messagesAPI } from '../services/api';
import {
  MessageSquare,
  PenSquare,
  Settings,
  Bell,
  BellOff,
  Shield,
  ChevronRight,
  X,
  Loader2,
  AlertCircle,
  Building2
} from 'lucide-react';

// Settings panel for buyers
function MessagingSettingsPanel({ buyerId, onClose }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await messagingService.getPermissions(buyerId);
        setSettings(data);
      } catch (err) {
        console.error('Error loading settings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadSettings();
  }, [buyerId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await messagingService.updatePermissions(buyerId, settings);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Messaging Settings</h3>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {settings && (
        <div className="space-y-6">
          {/* Allow messages toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Allow Messages</div>
              <div className="text-sm text-gray-500">Receive messages from sellers</div>
            </div>
            <button
              onClick={() => setSettings(s => ({ ...s, allow_messages: !s.allow_messages }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.allow_messages ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                settings.allow_messages ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>

          {/* Minimum match score */}
          <div>
            <div className="font-medium text-gray-900 mb-2">Minimum Match Score</div>
            <div className="text-sm text-gray-500 mb-3">
              Only receive messages from sellers with assets matching at least this score
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.min_match_score || 0}
              onChange={(e) => setSettings(s => ({ ...s, min_match_score: parseInt(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-1">
              <span>0% (All)</span>
              <span className="font-medium text-purple-600">{settings.min_match_score || 0}%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Rate limiting */}
          <div>
            <div className="font-medium text-gray-900 mb-2">Message Limit</div>
            <div className="text-sm text-gray-500 mb-3">
              Maximum messages per seller per day
            </div>
            <select
              value={settings.max_messages_per_day || 5}
              onChange={(e) => setSettings(s => ({ ...s, max_messages_per_day: parseInt(e.target.value) }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="1">1 message/day</option>
              <option value="3">3 messages/day</option>
              <option value="5">5 messages/day</option>
              <option value="10">10 messages/day</option>
              <option value="999">Unlimited</option>
            </select>
          </div>

          {/* Auto-reply */}
          <div>
            <div className="font-medium text-gray-900 mb-2">Auto-Reply</div>
            <div className="text-sm text-gray-500 mb-3">
              Send automatic response to initial messages
            </div>
            <textarea
              value={settings.auto_reply_message || ''}
              onChange={(e) => setSettings(s => ({ ...s, auto_reply_message: e.target.value }))}
              placeholder="Thank you for your message. I will review and respond shortly."
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg resize-none"
            />
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
}

// Compose new message modal
function ComposeModal({ userType, userId, onClose, onSuccess }) {
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Search for recipients
  const handleSearch = async (query) => {
    setRecipientSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Search for the opposite user type (buyers search for sellers, sellers search for buyers)
      const searchType = userType === 'buyer' ? 'seller' : 'buyer';
      const response = await messagesAPI.searchRecipients(query, searchType);
      setSearchResults(response.data?.data || []);
    } catch (err) {
      console.error('Error searching recipients:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  if (selectedRecipient) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <MessageComposer
            sellerId={userType === 'seller' ? userId : selectedRecipient.id}
            buyerId={userType === 'buyer' ? userId : selectedRecipient.id}
            buyerName={selectedRecipient.name}
            buyerCompany={selectedRecipient.company}
            senderType={userType}
            onSuccess={() => {
              onSuccess();
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">New Message</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search {userType === 'seller' ? 'Buyers' : 'Sellers'}
            </label>
            <input
              type="text"
              value={recipientSearch}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={`Enter ${userType === 'seller' ? 'buyer' : 'seller'} name or company...`}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {searching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          )}

          {searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map(result => (
                <button
                  key={result.id}
                  onClick={() => setSelectedRecipient(result)}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-medium">
                      {result.name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">{result.name}</div>
                    <div className="text-sm text-gray-500">{result.company}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          ) : recipientSearch.length >= 2 && !searching ? (
            <div className="text-center py-6 text-gray-500">
              <p>No results found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                Search for a {userType === 'seller' ? 'buyer' : 'seller'} to start a conversation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Messages page
export function Messages() {
  const { user, userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'thread'

  // Determine user type and ID
  const userType = userProfile?.user_type || 'seller';
  const userId = userType === 'seller' ? userProfile?.seller_id : userProfile?.buyer_id;

  // State for direct buyer messaging
  const [directBuyer, setDirectBuyer] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);

  // Handle URL params for direct conversation access
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    const buyerId = searchParams.get('buyerId');
    const buyerName = searchParams.get('buyerName');

    if (conversationId) {
      setSelectedConversation({ id: conversationId });
      setMobileView('thread');
    } else if (buyerId && userType === 'seller' && userId) {
      // Direct message to buyer - get or create conversation
      setLoadingConversation(true);
      setDirectBuyer({ id: buyerId, name: buyerName ? decodeURIComponent(buyerName) : 'Unknown Company' });

      messagingService.getOrCreateConversation(userId, buyerId)
        .then(result => {
          const convId = result?.id || result;
          if (convId) {
            setSelectedConversation({ id: convId, buyer_id: buyerId, buyerName: buyerName ? decodeURIComponent(buyerName) : null });
            setMobileView('thread');
            // Update URL to use conversation ID
            setSearchParams({ conversation: convId });
          }
        })
        .catch(err => {
          console.error('Error creating conversation:', err);
        })
        .finally(() => {
          setLoadingConversation(false);
        });
    }
  }, [searchParams, userId, userType]);

  // Handle conversation selection
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMobileView('thread');
    setSearchParams({ conversation: conversation.id });
  };

  // Handle back navigation on mobile
  const handleBack = () => {
    setSelectedConversation(null);
    setMobileView('list');
    setSearchParams({});
  };

  // Handle archive
  const handleArchive = async () => {
    if (!selectedConversation) return;
    try {
      await messagingService.archiveConversation(selectedConversation.id);
      handleBack();
    } catch (err) {
      console.error('Error archiving:', err);
    }
  };

  // Handle block (for buyers)
  const handleBlock = async () => {
    if (!selectedConversation || userType !== 'buyer') return;
    const sellerId = selectedConversation.seller_id;
    try {
      await messagingService.blockSeller(userId, sellerId);
      handleBack();
    } catch (err) {
      console.error('Error blocking:', err);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Required</h2>
          <p className="text-gray-500 mb-4">Please complete your profile to access messages.</p>
          <a
            href={userType === 'buyer' ? '/buyer/profile' : '/seller/profile'}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Complete Profile
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl font-bold text-gray-900">Messages</h1>
            </div>

            <div className="flex items-center gap-2">
              {/* Compose button */}
              <button
                onClick={() => setShowCompose(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <PenSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Compose</span>
              </button>

              {/* Settings button (for buyers) */}
              {userType === 'buyer' && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Messaging Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6 h-[calc(100vh-8rem)]">
          {/* Inbox panel */}
          <div className={`w-full lg:w-96 flex-shrink-0 ${
            mobileView === 'thread' ? 'hidden lg:block' : ''
          }`}>
            <MessageInbox
              userId={userId}
              userType={userType}
              selectedConversationId={selectedConversation?.id}
              onSelectConversation={handleSelectConversation}
              className="h-full"
            />
          </div>

          {/* Conversation panel */}
          <div className={`flex-1 ${
            mobileView === 'list' ? 'hidden lg:block' : ''
          }`}>
            {loadingConversation ? (
              <div className="h-full rounded-xl border border-gray-200 bg-white flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                <p className="text-gray-600 font-medium">Starting conversation with</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-gray-900 font-semibold">{directBuyer?.name || 'Loading...'}</span>
                </div>
              </div>
            ) : (
              <ConversationThread
                conversationId={selectedConversation?.id}
                userType={userType}
                onBack={handleBack}
                onArchive={handleArchive}
                onBlock={handleBlock}
                className="h-full rounded-xl border border-gray-200"
              />
            )}
          </div>
        </div>
      </div>

      {/* Compose modal */}
      {showCompose && (
        <ComposeModal
          userType={userType}
          userId={userId}
          onClose={() => setShowCompose(false)}
          onSuccess={() => {
            // Refresh will happen via real-time subscription
          }}
        />
      )}

      {/* Settings modal (for buyers) */}
      {showSettings && userType === 'buyer' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <MessagingSettingsPanel
              buyerId={userId}
              onClose={() => setShowSettings(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;
